/**
 * PractitionerCardHeader — Avatar + name + status badge + role/date row.
 *
 * Pending state uses a grey gradient override for the avatar
 * and an animated pulse dot on the badge.
 */

import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import { ClientAvatar } from '../practitioner-dashboard/ClientAvatar';

const GREY_GRADIENT: readonly [string, string] = ['#94a3b8', '#cbd5e1'];
const GRADIENT_START = { x: 0, y: 0 };
const GRADIENT_END = { x: 1, y: 1 };

interface PractitionerCardHeaderProps {
  initials: string;
  name: string;
  role: string;
  status: 'active' | 'pending';
  joinedAt: string;
  gradientIndex: number;
}

function formatDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return isoString;
  }
}

const PendingDot: React.FC = () => {
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.2, duration: 800, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1, duration: 800, useNativeDriver: true }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <Animated.View style={[styles.pendingDot, { opacity }]} accessibilityElementsHidden={true} />
  );
};

export const PractitionerCardHeader: React.FC<PractitionerCardHeaderProps> = React.memo(
  function PractitionerCardHeader({ initials, name, role, status, joinedAt, gradientIndex }) {
    const { t } = useTranslation('practitioner_consent');

    const isPending = status === 'pending';

    const dateLabel = formatDate(joinedAt);
    const subtitleKey = isPending ? 'card.role_invited' : 'card.role_since';
    const subtitle = t(subtitleKey, { role, date: dateLabel });

    const statusLabel = isPending ? t('card.status.pending') : t('card.status.active');

    return (
      <View style={styles.container}>
        {isPending ? (
          <LinearGradient
            colors={GREY_GRADIENT as [string, string]}
            start={GRADIENT_START}
            end={GRADIENT_END}
            style={styles.pendingAvatar}
            accessibilityElementsHidden={true}
          >
            <Text style={styles.avatarInitials} numberOfLines={1}>
              {initials.slice(0, 2).toUpperCase()}
            </Text>
          </LinearGradient>
        ) : (
          <ClientAvatar initials={initials} gradientIndex={gradientIndex} size={40} />
        )}

        <View style={styles.info}>
          <View style={styles.nameRow}>
            <Text style={[styles.name, isPending && styles.namePending]} numberOfLines={1}>
              {name}
            </Text>

            {isPending ? (
              <View style={styles.badgePending}>
                <PendingDot />
                <Text style={styles.badgePendingText}>{statusLabel}</Text>
              </View>
            ) : (
              <View style={styles.badgeActive}>
                <Text style={styles.badgeActiveText}>{statusLabel}</Text>
              </View>
            )}
          </View>

          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        </View>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: 'rgba(255, 255, 255, 0.4)',
  },
  pendingAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatarInitials: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 13,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  name: {
    fontSize: 14,
    fontWeight: '700',
    color: '#18181B',
    flexShrink: 1,
  },
  namePending: {
    color: '#52525B',
  },
  badgeActive: {
    backgroundColor: '#dcfce7',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  badgeActiveText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#15803d',
  },
  badgePending: {
    backgroundColor: '#fef3c7',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  badgePendingText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#92400e',
  },
  pendingDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#F59E0B',
  },
  subtitle: {
    fontSize: 12,
    color: '#A1A1AA',
    marginTop: 2,
  },
});
